import React, { useMemo, useState } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import { CurrencyDollar } from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';

function formatApiErrorDetail(detail) {
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e))).filter(Boolean).join(' ');
  }
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
}

export default function ResetPasswordPage() {
  const { user, loading, resetPassword } = useAuth();
  const [searchParams] = useSearchParams();
  const email = useMemo(() => searchParams.get('email') || '', [searchParams]);
  const token = useMemo(() => searchParams.get('token') || '', [searchParams]);
  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (loading) return null;
  if (user) return <Navigate to="/dashboard" />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');

    if (!email || !token) {
      setError('Reset link is invalid or incomplete.');
      return;
    }

    if (password !== passwordConfirmation) {
      setError('Password confirmation does not match.');
      return;
    }

    setSubmitting(true);

    try {
      const response = await resetPassword(email, token, password, passwordConfirmation);
      setMessage(response?.message || 'Password has been reset successfully.');
      setPassword('');
      setPasswordConfirmation('');
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FBF9F6] flex items-center justify-center p-4" data-testid="reset-password-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8 animate-fade-in">
          <div className="inline-flex items-center justify-center w-14 h-14 bg-[#1E3F2D] rounded-2xl mb-4">
            <CurrencyDollar size={28} weight="bold" className="text-white" />
          </div>
          <h1 className="text-3xl font-extrabold tracking-tight text-[#1C1B1A]" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Reset Password
          </h1>
          <p className="text-[#5C5A56] mt-1 text-sm">Set a new password for {email || 'your account'}.</p>
        </div>

        <div className="bg-white border border-[#E8E6E1] rounded-2xl shadow-sm p-8 animate-fade-in">
          {message && (
            <div className="bg-green-50 border border-green-200 text-[#265C42] text-sm rounded-lg p-3 mb-4">
              {message}
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 text-[#A63C24] text-sm rounded-lg p-3 mb-4">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="reset-password" className="block text-xs font-bold uppercase tracking-[0.2em] text-[#5C5A56] mb-2">New Password</label>
              <input
                id="reset-password"
                name="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-white border border-[#E8E6E1] text-[#1C1B1A] rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#1E3F2D]/20 focus:border-[#1E3F2D] transition-all"
                placeholder="Min 6 characters"
                minLength={6}
                required
              />
            </div>
            <div>
              <label htmlFor="reset-password-confirmation" className="block text-xs font-bold uppercase tracking-[0.2em] text-[#5C5A56] mb-2">Confirm Password</label>
              <input
                id="reset-password-confirmation"
                name="password_confirmation"
                type="password"
                value={passwordConfirmation}
                onChange={(e) => setPasswordConfirmation(e.target.value)}
                className="w-full bg-white border border-[#E8E6E1] text-[#1C1B1A] rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#1E3F2D]/20 focus:border-[#1E3F2D] transition-all"
                placeholder="Re-enter password"
                minLength={6}
                required
              />
            </div>
            <button
              type="submit"
              disabled={submitting}
              className="w-full bg-[#1E3F2D] text-white font-semibold rounded-xl px-6 py-3 hover:bg-[#29563E] transition-colors active:scale-[0.98] disabled:opacity-60"
            >
              {submitting ? 'Resetting password...' : 'Reset Password'}
            </button>
          </form>

          <p className="text-center text-sm text-[#5C5A56] mt-6">
            Back to{' '}
            <Link to="/login" className="text-[#1E3F2D] font-semibold hover:underline">
              Sign In
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
